import { useParams } from 'react-router-dom'
import { Alert, Avatar, Group, SimpleGrid, Stack, Text, Title } from 'components'
import { useArcTranslation } from 'hooks'
import { getInitials } from 'helpers'
import { ParticipantsSkeleton } from './ParticipantsSkeleton'
import { useProjectParticipants } from './useProjectParticipants'
import type { ProjectStudent, ProjectTeacher } from './types'

const GRID_BREAKPOINTS = [
  { maxWidth: 'md', cols: 2 },
  { maxWidth: 'sm', cols: 1 },
]

const SubLabel = ({ children }: { readonly children: React.ReactNode }) => (
  <Text size='xs' transform='uppercase' weight={600} color='dimmed'>
    {children}
  </Text>
)

const PersonRow = ({
  fullName,
  avatarUrl,
  color = 'blue',
}: {
  readonly fullName: string
  readonly avatarUrl: string | null
  readonly color?: string
}) => (
  <Group spacing='sm' noWrap>
    <Avatar src={avatarUrl} radius='xl' size='md' color={color}>
      {getInitials(fullName)}
    </Avatar>
    <Text size='sm' weight={500} lineClamp={2}>
      {fullName}
    </Text>
  </Group>
)

const TeacherSubSection = ({
  label,
  teachers,
}: {
  readonly label: string
  readonly teachers: readonly ProjectTeacher[]
}) => (
  <Stack spacing='xs'>
    <SubLabel>{label}</SubLabel>
    <SimpleGrid cols={4} breakpoints={GRID_BREAKPOINTS} spacing='sm'>
      {teachers.map(t => (
        <PersonRow key={t.id} fullName={t.fullName} avatarUrl={t.avatarUrl} />
      ))}
    </SimpleGrid>
  </Stack>
)

export const ProjectParticipantsTab = () => {
  const { id } = useParams()
  const { t } = useArcTranslation()
  const { creator, coTeachers, students, isLoading, isError } = useProjectParticipants(id)

  if (isLoading) return <ParticipantsSkeleton />

  if (isError) {
    return <Alert color='red'>{t('arc_project_participants.error_generic')}</Alert>
  }

  return (
    <Stack spacing='xl'>
      <Stack spacing='md'>
        <Title order={4}>{t('arc_project_participants.teachers')}</Title>
        <Stack spacing='lg'>
          {creator && (
            <Stack spacing='xs'>
              <SubLabel>{t('arc_project_participants.created_by')}</SubLabel>
              <PersonRow fullName={creator.fullName} avatarUrl={creator.avatarUrl} />
            </Stack>
          )}
          {coTeachers.length > 0 && (
            <TeacherSubSection label={t('arc_project_participants.co_teachers')} teachers={coTeachers} />
          )}
        </Stack>
      </Stack>

      <Stack spacing='md'>
        <Title order={4}>{t('arc_project_participants.students')}</Title>
        {students.length === 0 ? (
          <Text size='sm' color='dimmed'>
            {t('arc_project_participants.empty_students')}
          </Text>
        ) : (
          <SimpleGrid cols={4} breakpoints={GRID_BREAKPOINTS} spacing='sm'>
            {students.map((s: ProjectStudent) => (
              <PersonRow key={s.id} fullName={s.fullName} avatarUrl={s.avatarUrl} />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Stack>
  )
}
